import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';

import { FeatureGateService } from 'src/shared/services/feature-gate.service';

import { SendForSignaturePage } from 'src/pages/compliance/send-for-signature/send-for-signature';

import { RentalApplicationsService, RentalApplication } from 'src/shared/services/rental-applications.service';
import { RentalUnitsService } from 'src/shared/services/rental-units.service';

@Component({
  templateUrl: 'application-detail.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ApplicationDetailPage implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private util = inject(Util);
  private featureGate = inject(FeatureGateService);
  private applicationsService = inject(RentalApplicationsService);
  private rentalUnitsService = inject(RentalUnitsService);

  readonly windowService = inject(WindowService);

  isRightSideContent = true;
  dialogParams: any;
  application: RentalApplication;
  loaded = false;
  errorMessage: string;
  private panelClosedSubscription: Subscription;

  // Precomputed on load — never called from the template — per the by-room
  // screening policy of the unit this application is for (undefined unit or
  // 'entire' rental_mode leaves both false/empty, so whole-unit applications
  // render exactly as they do today).
  isByRoomUnit = false;
  showPolicyBanner = false;
  policyBannerText = '';

  approveAction = () => this.approve();
  declineAction = () => this.decline();
  sendLeaseAction = () => this.sendLeaseForSignature();

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.dialogParams = await this.storage.retrieve('dialog-params');
    this.application = this.navCtrl.getParam('application') || this.dialogParams?.application;
    if (this.isRightSideContent && this.application) {
      this.rightSidePanelService.setDialogPageTitle(this.application.applicant_name);
    }
    if (this.application?.property_id) {
      const unit = await this.rentalUnitsService.getUnit(this.application.property_id);
      this.isByRoomUnit = !!unit && this.rentalUnitsService.isByRoom(unit);
      this.showPolicyBanner = this.isByRoomUnit && unit?.screening_policy === 'every_member_every_move_in';
      this.policyBannerText = 'By-the-room unit — every member is screened on every move-in.';
    }
    // Defer to the next tick so the view paints after the panel has attached
    // this component to the change-detection tree (nested panel → panel nav).
    setTimeout(() => {
      this.loaded = true;
      this.cdr.markForCheck();
    });
  }

  getRecommendationLabel(): string {
    switch (this.application?.screening?.recommendation) {
      case 'recommended': return 'Recommended';
      case 'review': return 'Review Recommended';
      case 'not_recommended': return 'Not Recommended';
      default: return 'Screening In Progress';
    }
  }

  getRecommendationColor(): string {
    switch (this.application?.screening?.recommendation) {
      case 'recommended': return '#2E7D32';
      case 'review': return '#F9A825';
      case 'not_recommended': return '#C62828';
      default: return '#999';
    }
  }

  getCreditBand(score: number | null): string {
    if (score === null) { return ''; }
    if (score >= 740) { return 'Excellent'; }
    if (score >= 670) { return 'Good'; }
    if (score >= 580) { return 'Fair'; }
    return 'Poor';
  }

  getCheckColor(value: string): string {
    switch (value) {
      case 'clear':
      case 'none': return '#2E7D32';
      case 'records_found': return '#C62828';
      default: return '#999';
    }
  }

  getCheckLabel(value: string): string {
    switch (value) {
      case 'clear': return 'Clear — no records';
      case 'none': return 'None found';
      case 'records_found': return 'Records found';
      case 'pending': return 'Pending';
      default: return value;
    }
  }

  async approve() {
    this.featureGate.gate();
  }

  async decline() {
    this.featureGate.gate();
  }

  private async updateStatus(status: RentalApplication['status'], message: string) {
    try {
      await this.applicationsService.updateApplicationStatus(this.application.id, status);
      this.application = { ...this.application, status };
      this.util.showSuccess(message);
      this.cdr.markForCheck();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message;
      this.cdr.markForCheck();
    }
  }

  async sendLeaseForSignature() {
    if (this.featureGate.gate()) { return; }
    await this.updateStatus('Lease Sent', '');
    const agreement = {
      id: this.application.id,
      name: `Lease — ${this.application.property_name} ${this.application.unit}`,
      created_at: '',
      updated_at: '',
      status: 'active',
    };
    this.goToPage('send-for-signature', { agreement }, SendForSignaturePage);
  }

  async goToPage(url: string, params: any, component: any) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.cdr.markForCheck();
      });
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }
}
